/**
 * TanStack Query hooks for followers/following lists
 *
 * Provides cache-first behavior with:
 * - useInfiniteQuery for cursor-based pagination
 * - viewerBucket pattern (cache varies by viewer for is_following/is_mutual fields)
 * - No reload on tab switch (cached data persists)
 * - staleTime: 60s for reasonable freshness
 *
 * NOTE: These are NOT persisted to IndexedDB (unbounded lists would bloat cache)
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type Query } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { SearchResponse } from './useSearchAllQuery';
import type { RecentSearchItem } from './useRecentSearchesQuery';

/**
 * User in followers/following list
 */
export interface FollowUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  followers_count: number;
  following_count: number;
  followed_at: string;
  is_following: boolean;
  is_mutual: boolean;
}

/**
 * API response for followers/following list
 */
interface FollowListResponse {
  users: FollowUser[];
  next_cursor: string | null;
  has_more: boolean;
}

const PAGE_SIZE = 20;
const STALE_TIME = 60 * 1000; // 60 seconds

/**
 * Infinite query for a user's followers
 *
 * @param profileId - The user ID whose followers to fetch
 */
export function useFollowersInfinite(profileId: string | null) {
  const { user, session } = useAuth();
  const viewerBucket = user?.id ?? 'anon';
  const token = session?.access_token;

  return useInfiniteQuery({
    queryKey: queryKeys.users.followers(profileId ?? '', viewerBucket),
    enabled: !!profileId,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam != null) params.set('cursor', pageParam);

      return apiFetch<FollowListResponse>(
        `/users/${profileId}/followers?${params}`,
        { token, signal }
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    staleTime: STALE_TIME,
  });
}

/**
 * Infinite query for users a user is following
 *
 * @param profileId - The user ID whose following list to fetch
 */
export function useFollowingInfinite(profileId: string | null) {
  const { user, session } = useAuth();
  const viewerBucket = user?.id ?? 'anon';
  const token = session?.access_token;

  return useInfiniteQuery({
    queryKey: queryKeys.users.following(profileId ?? '', viewerBucket),
    enabled: !!profileId,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam != null) params.set('cursor', pageParam);

      return apiFetch<FollowListResponse>(
        `/users/${profileId}/following?${params}`,
        { token, signal }
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    staleTime: STALE_TIME,
  });
}

/**
 * Follow status response shape from API
 */
export interface FollowStatusData {
  follows_viewer: boolean;
  viewer_follows_target: boolean;
  is_mutual: boolean;
}

/**
 * Query for follow status between viewer and a target user
 *
 * @param targetUserId - The user to check follow status for
 * @param options - Optional query options (e.g., refetchInterval for polling)
 */
export function useFollowStatus(
  targetUserId: string | null,
  options?: { refetchInterval?: number }
) {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;

  return useQuery({
    queryKey: queryKeys.users.followStatus(targetUserId ?? '', viewerId ?? ''),
    enabled: !!targetUserId && !!viewerId && !!token,
    queryFn: async ({ signal }) => {
      const response = await apiFetch<FollowStatusData>(
        `/users/${targetUserId}/follow-status`,
        { token, signal }
      );
      return response;
    },
    staleTime: STALE_TIME,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Snapshot + patch helper for search cache entries.
 * Returns a snapshot for rollback and patches user results in all cached search queries.
 *
 * @param queryClient - TanStack QueryClient
 * @param targetUserId - The target user being followed/unfollowed
 * @param viewerId - The viewer performing the action
 * @param delta - +1 for follow, -1 for unfollow
 */
function patchSearchCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  targetUserId: string,
  viewerId: string,
  delta: 1 | -1,
): Array<{ queryKey: readonly unknown[]; data: SearchResponse | undefined }> {
  // Snapshot all search cache entries for rollback
  const searchQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['search', 'all'] }) as Query<SearchResponse>[];

  const snapshot = searchQueries.map((q) => ({
    queryKey: q.queryKey,
    data: q.state.data as SearchResponse | undefined,
  }));

  // Patch each cached search response
  for (const query of searchQueries) {
    const data = query.state.data as SearchResponse | undefined;
    if (!data?.results?.length) continue;

    const hasTarget = data.results.some(
      (r) => r.entity_type === 'user' && String(r.id) === targetUserId
    );
    const hasViewer = data.results.some(
      (r) => r.entity_type === 'user' && String(r.id) === viewerId
    );

    if (!hasTarget && !hasViewer) continue;

    queryClient.setQueryData<SearchResponse>(query.queryKey, {
      ...data,
      results: data.results.map((r) => {
        if (r.entity_type !== 'user') return r;
        const meta = { ...(r.meta as Record<string, unknown>) };

        if (String(r.id) === targetUserId) {
          // Target gains/loses a follower
          meta.followers_count = Math.max(0, Number(meta.followers_count ?? 0) + delta);
        } else if (String(r.id) === viewerId) {
          // Viewer gains/loses a following
          meta.following_count = Math.max(0, Number(meta.following_count ?? 0) + delta);
        } else {
          return r;
        }

        // Rebuild subtitle from patched counts to keep display in sync
        const username = String(meta.username ?? '');
        const followers = Number(meta.followers_count ?? 0);
        const following = Number(meta.following_count ?? 0);
        const subtitle = `@${username} • ${followers} followers • ${following} following`;

        return { ...r, meta, subtitle };
      }),
    });
  }

  return snapshot;
}

/**
 * Restore search caches from a snapshot (used on optimistic update rollback)
 */
function rollbackSearchCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: Array<{ queryKey: readonly unknown[]; data: SearchResponse | undefined }>,
) {
  for (const entry of snapshot) {
    queryClient.setQueryData(entry.queryKey, entry.data);
  }
}

/**
 * Snapshot + patch helper for recent-search cache entries.
 * Patches user recents where the target gains/loses followers or viewer gains/loses following.
 */
function patchRecentsCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  targetUserId: string,
  viewerId: string,
  delta: 1 | -1,
): { queryKey: readonly unknown[]; data: RecentSearchItem[] | undefined } | null {
  const queryKey = queryKeys.user.recentSearches(viewerId);
  const data = queryClient.getQueryData<RecentSearchItem[]>(queryKey);
  if (!data?.length) return null;

  const hasRelevant = data.some(
    (item) => item.media_type === 'user' && (item.id === targetUserId || item.id === viewerId)
  );
  if (!hasRelevant) return null;

  const snapshot = { queryKey, data };

  queryClient.setQueryData<RecentSearchItem[]>(queryKey, data.map((item) => {
    if (item.media_type !== 'user') return item;
    const meta = { ...item.meta };

    if (item.id === targetUserId) {
      meta.followers_count = Math.max(0, Number(meta.followers_count ?? 0) + delta);
    } else if (item.id === viewerId) {
      meta.following_count = Math.max(0, Number(meta.following_count ?? 0) + delta);
    } else {
      return item;
    }

    return { ...item, meta };
  }));

  return snapshot;
}

/**
 * Restore recent-search cache from a snapshot (used on optimistic update rollback)
 */
function rollbackRecentsCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: { queryKey: readonly unknown[]; data: RecentSearchItem[] | undefined } | null,
) {
  if (!snapshot) return;
  queryClient.setQueryData(snapshot.queryKey, snapshot.data);
}

/**
 * Mutation to follow a user with optimistic updates
 */
export function useFollowMutation() {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!token) throw new Error('Not authenticated');
      await apiFetch(`/users/${targetUserId}/follow`, {
        token,
        method: 'POST',
      });
    },
    onMutate: async (targetUserId: string) => {
      if (!viewerId) return;

      // Update follow status cache with object shape
      const statusKey = queryKeys.users.followStatus(targetUserId, viewerId);
      await queryClient.cancelQueries({ queryKey: statusKey });
      const previousStatus = queryClient.getQueryData<FollowStatusData>(statusKey);

      // Optimistic update: viewer now follows target
      const follows_viewer = previousStatus?.follows_viewer ?? false;
      queryClient.setQueryData<FollowStatusData>(statusKey, {
        follows_viewer,
        viewer_follows_target: true,
        is_mutual: follows_viewer && true,
      });

      // Optimistic patch: increment followers_count on target, following_count on viewer
      const searchSnapshot = patchSearchCaches(queryClient, targetUserId, viewerId, 1);
      const recentsSnapshot = patchRecentsCaches(queryClient, targetUserId, viewerId, 1);

      return { previousStatus, statusKey, searchSnapshot, recentsSnapshot };
    },
    onError: (_err, _targetUserId, context) => {
      if (context?.statusKey) {
        queryClient.setQueryData(context.statusKey, context.previousStatus);
      }
      if (context?.searchSnapshot) {
        rollbackSearchCaches(queryClient, context.searchSnapshot);
      }
      rollbackRecentsCaches(queryClient, context?.recentsSnapshot ?? null);
    },
    onSettled: (_data, _error, targetUserId) => {
      if (!viewerId) return;

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.followStatus(targetUserId, viewerId),
      });
      // Invalidate the target user's followers list (they gained a follower)
      queryClient.invalidateQueries({
        queryKey: ['users', targetUserId, 'followers'],
        exact: false,
      });
      // Invalidate viewer's following list (viewer followed someone)
      queryClient.invalidateQueries({
        queryKey: ['users', viewerId, 'following'],
        exact: false,
      });
      // NOTE: Do NOT invalidate search/recents caches here.
      // The optimistic patch (onMutate) already has the correct counts.
      // Invalidating would refetch from Meilisearch before the reindex queue
      // processes, overwriting the correct optimistic values with stale data.
    },
  });
}

/**
 * Mutation to unfollow a user with optimistic updates
 */
export function useUnfollowMutation() {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!token) throw new Error('Not authenticated');
      await apiFetch(`/users/${targetUserId}/follow`, {
        token,
        method: 'DELETE',
      });
    },
    onMutate: async (targetUserId: string) => {
      if (!viewerId) return;

      // Update follow status cache with object shape
      const statusKey = queryKeys.users.followStatus(targetUserId, viewerId);
      await queryClient.cancelQueries({ queryKey: statusKey });
      const previousStatus = queryClient.getQueryData<FollowStatusData>(statusKey);

      // Optimistic update: viewer no longer follows target
      const follows_viewer = previousStatus?.follows_viewer ?? false;
      queryClient.setQueryData<FollowStatusData>(statusKey, {
        follows_viewer,
        viewer_follows_target: false,
        is_mutual: false, // Can't be mutual if viewer doesn't follow
      });

      // Optimistic patch: decrement followers_count on target, following_count on viewer
      const searchSnapshot = patchSearchCaches(queryClient, targetUserId, viewerId, -1);
      const recentsSnapshot = patchRecentsCaches(queryClient, targetUserId, viewerId, -1);

      return { previousStatus, statusKey, searchSnapshot, recentsSnapshot };
    },
    onError: (_err, _targetUserId, context) => {
      if (context?.statusKey) {
        queryClient.setQueryData(context.statusKey, context.previousStatus);
      }
      if (context?.searchSnapshot) {
        rollbackSearchCaches(queryClient, context.searchSnapshot);
      }
      rollbackRecentsCaches(queryClient, context?.recentsSnapshot ?? null);
    },
    onSettled: (_data, _error, targetUserId) => {
      if (!viewerId) return;

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.followStatus(targetUserId, viewerId),
      });
      // Invalidate the target user's followers list (they lost a follower)
      queryClient.invalidateQueries({
        queryKey: ['users', targetUserId, 'followers'],
        exact: false,
      });
      // Invalidate viewer's following list (viewer unfollowed someone)
      queryClient.invalidateQueries({
        queryKey: ['users', viewerId, 'following'],
        exact: false,
      });
      // NOTE: Do NOT invalidate search/recents caches here.
      // The optimistic patch (onMutate) already has the correct counts.
      // Invalidating would refetch from Meilisearch before the reindex queue
      // processes, overwriting the correct optimistic values with stale data.
    },
  });
}

/**
 * Helper to flatten pages from useInfiniteQuery into a single array
 */
export function flattenFollowPages(
  pages: FollowListResponse[] | undefined
): FollowUser[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.users);
}
